import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'helpers/redux';
import { RootState } from 'app/store';
import { fetchCatalogList } from '../catalog.reducer';
import { CatalogItem } from '../models';
import CatalogStats from '../components/CatalogStats';
import CatalogTable from '../components/CatalogTable';
import UploadCatalogModal from '../components/UploadCatalogModal';
import Button from 'components/Button/Button';
import {
    RiSearchLine,
    RiFilter3Line,
    RiRefreshLine,
    RiAddLine,
    RiBookOpenLine,
} from 'react-icons/ri';
import { UserCredential } from 'features/login/models';

const CATEGORY_OPTIONS = [
    'All Categories',
    'SOP',
    'Training',
    'Policy',
    'Work Instruction',
    'OCALC',
    'Template PPT',
];

const STATUS_OPTIONS = ['All Status', 'Draft', 'Published', 'Archived'];

const CatalogPage: React.FC = () => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const [isUploadOpen, setIsUploadOpen] = useState<boolean>(false);
    const [refreshKey, setRefreshKey] = useState<number>(0);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [selectedCategory, setSelectedCategory] = useState<string>(
        CATEGORY_OPTIONS[0],
    );
    const [selectedStatus, setSelectedStatus] = useState<string>(
        STATUS_OPTIONS[0],
    );

    const catalogList = useAppSelector(
        (state: RootState) => state.catalog.list ?? [],
    );
    const isLoading = useAppSelector(
        (state: RootState) => state.catalog.isLoading ?? false,
    );
    const user = useAppSelector(
        (state: RootState) => state.user.userAuth as UserCredential,
    );

    const canUpload =
        user?.roleName?.some(
            (r) =>
                r.toUpperCase().includes('SPV') ||
                r.toLowerCase() === 'superadmin',
        ) || false;

    const catIdx = CATEGORY_OPTIONS.indexOf(selectedCategory);
    const statIdx = STATUS_OPTIONS.indexOf(selectedStatus);
    const currentCategory = catIdx > 0 ? catIdx - 1 : undefined;
    const currentStatus = statIdx > 0 ? statIdx - 1 : undefined;

    const handleResetFilter = () => {
        setSearchQuery('');
        setSelectedCategory(CATEGORY_OPTIONS[0]);
        setSelectedStatus(STATUS_OPTIONS[2]);
    };

    const handleSelectDetail = (item: CatalogItem) => {
        navigate(`/catalog/${item.id}`);
    };

    const handleFilterPending = () => {
        setSelectedStatus('Draft');
    };

    return (
        <div className="p-4 md:p-8 bg-gray-50/50 dark:bg-gray-900 min-h-screen">
            {/* Header section */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 bg-white dark:bg-gray-800 p-6 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm">
                <div>
                    <div className="flex items-center space-x-2.5">
                        <div className="w-10 h-10 rounded-xl bg-[#4c8b6a]/10 dark:bg-[#4c8b6a]/20 text-[#4c8b6a] flex items-center justify-center text-xl font-bold">
                            <RiBookOpenLine />
                        </div>
                        <div>
                            <h1 className="text-xl font-bold text-gray-800 dark:text-white leading-tight">
                                Catalog & Document Management
                            </h1>
                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                Centralized document repository with version
                                control and workflow approval
                            </p>
                        </div>
                    </div>
                </div>

                <div className="flex items-center space-x-3">
                    <button
                        onClick={handleResetFilter}
                        title="Refresh list"
                        className="p-2.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300 rounded-xl transition-colors flex items-center"
                    >
                        <RiRefreshLine
                            size={18}
                            className={isLoading ? 'animate-spin' : ''}
                        />
                    </button>

                    {canUpload ? (
                        <button
                            onClick={() => setIsUploadOpen(true)}
                            className="inline-flex items-center px-4 py-2.5 bg-[#4c8b6a] hover:bg-[#3d7055] text-white text-sm font-semibold rounded-lg shadow-sm transition-all duration-200"
                        >
                            <RiAddLine className="mr-1.5 text-lg" />
                            Upload Catalog
                        </button>
                    ) : (
                        <div
                            title="Only SPV role can upload documents"
                            className="inline-flex items-center px-4 py-2.5 bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 text-sm font-semibold rounded-xl cursor-not-allowed select-none"
                        >
                            <RiAddLine className="mr-1.5 text-lg" />
                            Upload Catalog (SPV Only)
                        </div>
                    )}
                </div>
            </div>

            {/* Statistics Cards */}
            <CatalogStats
                list={catalogList}
                onFilterPending={handleFilterPending}
            />

            {/* Filter Toolbar */}
            <div className="bg-white dark:bg-gray-800 p-4 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm mb-6">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-center">
                    {/* Search bar */}
                    <div className="md:col-span-6 relative">
                        <RiSearchLine className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 text-lg" />
                        <input
                            type="text"
                            placeholder="Search documents by title, filename, or description..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2.5 bg-gray-50 dark:bg-gray-700/60 border border-gray-200 dark:border-gray-600 rounded-xl text-sm text-gray-800 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#4c8b6a] dark:focus:ring-[#4c8b6a] transition-all"
                        />
                    </div>

                    {/* Category Filter */}
                    <div className="md:col-span-3 flex items-center space-x-2">
                        <RiFilter3Line className="text-gray-400 shrink-0 text-lg hidden sm:inline" />
                        <select
                            value={selectedCategory}
                            onChange={(e) =>
                                setSelectedCategory(e.target.value)
                            }
                            className="w-full py-2.5 px-3 bg-gray-50 dark:bg-gray-700/60 border border-gray-200 dark:border-gray-600 rounded-xl text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-[#4c8b6a]"
                        >
                            {CATEGORY_OPTIONS.map((cat) => (
                                <option key={cat} value={cat}>
                                    {cat}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Status Filter */}
                    <div className="md:col-span-3">
                        <select
                            value={selectedStatus}
                            onChange={(e) => setSelectedStatus(e.target.value)}
                            className="w-full py-2.5 px-3 bg-gray-50 dark:bg-gray-700/60 border border-gray-200 dark:border-gray-600 rounded-xl text-sm text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-[#4c8b6a]"
                        >
                            {STATUS_OPTIONS.map((st) => (
                                <option key={st} value={st}>
                                    {st}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
            </div>

            {/* Main Catalog Table */}
            <div className="transition-opacity duration-300">
                <CatalogTable
                    onSelectDetail={handleSelectDetail}
                    filter={searchQuery || undefined}
                    category={currentCategory}
                    status={currentStatus}
                    refreshTrigger={refreshKey}
                />
            </div>

            {/* Upload Modal Drawer */}
            <UploadCatalogModal
                isOpen={isUploadOpen}
                onClose={() => setIsUploadOpen(false)}
                onSuccess={() => setRefreshKey((prev) => prev + 1)}
            />
        </div>
    );
};

export default CatalogPage;
